import { List, Map } from "immutable";
import { isNil } from "lodash";
import { createSelector } from "reselect";

import { ConstraintComparison, ConstraintResolutionOption, ConstraintTypes } from "../../constants/constraints";
import { PropertyFormatIdentifier } from "../../constants/propertyFormats";
import { definitionSelectors } from "../../modules/definition";
import { calendarSelectors } from "../../modules/definition/calendar";
import { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import dateByAddingSpan from "../../modules/definition/calendar/dateFunctions/dateByAddingSpan";
import dateSubtractionFunctions from "../../modules/definition/calendar/dateFunctions/dateSubtractionFunctions";
import dateWithTimestamp from "../../modules/definition/calendar/dateFunctions/dateWithTimestamp";
import { stringFromDate } from "../../modules/definition/calendar/dateFunctions/stringFromDate";
import {
  getReverseTimespan,
  stringFromTimespan
} from "../../modules/definition/calendar/dateFunctions/timespanFunctions";
import { propertySelectors } from "../../modules/definition/properties";
import { referenceSelectors } from "../../modules/definition/references";
import { typeSelectors } from "../../modules/definition/types";
import displayCacheSelectors from "../../modules/display/selectors";
import { itemSelectors } from "../../modules/items";
import sortFunctions from "../../modules/items/sortFunctions";
import { narrativeSelectors } from "../../modules/narrative";
import superAndChildrenSelectors from "../../modules/superAndChildrenById/selectors";
import { uiStateSelectors } from "../../modules/uiState";
import { createFromJS, immutableRecords } from "../../types";
import { mapPick } from "../../utils/immutableExtensions";
import { getLabelWithUntitled } from "../../utils/labelUtils";
import uniqueId from "../../utils/uniqueId";

const getItemsById = state => state.getIn(["data", "items", "byId"]);
const getRelationships = state => state.getIn(["data", "relationships", "byId"]);
const getNarratives = state => state.getIn(["data", "narrative"]);

const getTypeFromDefinitions = (state, id) => definitionSelectors.getTypesById(state).get(id);
const getItemlabel = (itemsById, id) => itemsById.getIn([id, "label"]);
const getItemStartDate = (itemsById, id) => itemsById.getIn([id, "startDate"]);
const getItemDuration = (itemsById, id) => itemsById.getIn([id, "duration"]);
const getItemIsOngoing = (itemsById, id) => itemsById.getIn([id, "ongoing"]);

const getReverseComparison = comparison =>
  comparison === ConstraintComparison.After
    ? ConstraintComparison.Before
    : comparison === ConstraintComparison.Before
      ? ConstraintComparison.After
      : ConstraintComparison.Equal;

const relativeConstraintResolutions = List([
  immutableRecords.InspectorConstraintResolutionRecord({
    id: ConstraintResolutionOption.MoveSelf,
    label: `Move selected items`
  }),
  immutableRecords.InspectorConstraintResolutionRecord({
    id: ConstraintResolutionOption.MoveOther,
    label: "Move other item to match selected"
  })
]);

const relativeConstraintSelectedMissingDateResolutions = List([
  immutableRecords.InspectorConstraintResolutionRecord({
    id: ConstraintResolutionOption.MoveSelf,
    label: `Assign date to selected items`
  })
]);

const relativeConstraintOtherMissingDateResolutions = List([
  immutableRecords.InspectorConstraintResolutionRecord({
    id: ConstraintResolutionOption.MoveOther,
    label: "Assign date to other item"
  })
]);

const fixedConstraintResolutions = List([
  immutableRecords.InspectorConstraintResolutionRecord({
    id: ConstraintResolutionOption.MoveSelf,
    label: "Move to fit constraint"
  })
]);

// const getDenormalisedRelativeConstraintRecordForItem = (state, obj, constraint) => {
//   return getDenormalisedRelativeConstraintRecordForItemWithoutState(getItemsById(state), obj, constraint);
// };

const getRelativeResolutionOptions = (item, otherItem) =>
  isDateValid(item.startDate) && isDateValid(otherItem.startDate)
    ? relativeConstraintResolutions
    : isDateValid(item.startDate)
      ? relativeConstraintOtherMissingDateResolutions
      : relativeConstraintSelectedMissingDateResolutions;

const getDenormalisedRelativeConstraintRecordForItemWithoutState = (itemsById, obj, constraint) => {
  // TODO: Need to add ability to reverse constraint if it is the object instead of the subject.
  if (constraint.subject === obj.id) {
    // We are the subject, so access the object as normal...
    const other = constraint.object;
    const otherItem = itemsById.get(other);
    const resolutionOptions = getRelativeResolutionOptions(obj, otherItem);

    return createFromJS.InspectorConstraintRecord(constraint).merge({
      ids: List([constraint.id]),
      thisTarget: constraint.subjectTarget,
      otherTarget: constraint.objectTarget,
      other,
      otherLabel: getItemlabel(itemsById, other),
      offset: immutableRecords
        .TimespanDisplayRecord(constraint.offset)
        .set("label", stringFromTimespan(constraint.offset)),
      resolutionOptions
    });
  } else {
    // Otherwise, we are the object, so everything needs to be flipped on its head
    const other = constraint.subject;
    const otherItem = itemsById.get(other);
    const resolutionOptions = getRelativeResolutionOptions(obj, otherItem);
    const offset = getReverseTimespan(constraint.offset);
    return createFromJS.InspectorConstraintRecord(constraint).merge({
      ids: List([constraint.id]),
      thisTarget: constraint.objectTarget,
      otherTarget: constraint.subjectTarget,
      other,
      comparison: getReverseComparison(constraint.comparison),
      otherLabel: getItemlabel(itemsById, other),
      offset: immutableRecords.TimespanDisplayRecord(offset).set("label", stringFromTimespan(offset)),
      resolutionOptions
    });
  }
};

const getDenormalisedConstraintRecordForItemWithoutState = (itemsById, calendar, obj, validatedConstraint) => {
  const c = validatedConstraint;
  if (c.constraintType === ConstraintTypes.Relative) {
    return getDenormalisedRelativeConstraintRecordForItemWithoutState(itemsById, obj, c);
  } else if (c.constraintType === ConstraintTypes.Date) {
    return createFromJS.InspectorConstraintRecord(c).merge({
      ids: List([c.id]),
      thisTarget: c.subjectTarget,
      date: immutableRecords.DateDisplayRecord(c.date).set("label", stringFromDate(calendar, c.date)),
      resolutionOptions: fixedConstraintResolutions
    });
  } else if (c.constraintType === ConstraintTypes.DatePart) {
    return createFromJS.InspectorConstraintRecord(c).merge({
      ids: List([c.id]),
      thisTarget: c.subjectTarget
    });
  }
};

/**
 * @param {TimelineRecord} state
 */
const getDenormalisedSelectedItems = createSelector(
  // inputs
  displayCacheSelectors.getDisplayItemsById,
  uiStateSelectors.getSelectedItems,
  calendarSelectors.getCalendar,
  definitionSelectors.getColorMap,
  displayCacheSelectors.getValidatedDependencies,
  displayCacheSelectors.getValidatedDateConstraints,
  definitionSelectors.getPropertiesById,
  definitionSelectors.getReferencesById,
  getRelationships,
  getNarratives,
  typeSelectors.getTypesById,
  itemSelectors.getItemIndexesInTreeOrderForAllTypes,
  // calculate based on inputs
  (
    displayItemsById,
    selectedItemIds,
    calendar,
    colorMap,
    validatedDependencies,
    validatedConstraints,
    properties,
    references,
    relationships,
    narratives,
    typesById,
    globalItemIndexes
  ) => {
    const result = selectedItemIds.map(sid => {
      const obj = displayItemsById.get(sid);
      const recmap = Map(obj).withMutations(rm => {
        // For later use
        const startDate = obj.startDate;

        const children = displayItemsById.get(obj.id).children;

        rm.set("children", children);

        if (children.size > 0) {
          rm.set("hasChildren", true);
        }

        if (typeSelectors.isAvatarType(typesById.get(obj.type))) {
          const initials = itemSelectors.getInitialsForItem(obj);
          if (initials.length) {
            rm.set("initials", initials);
          }
        }

        if (typeSelectors.isShortLabelType(typesById.get(obj.type))) {
          const shortLabel = obj.shortLabel ? itemSelectors.getShortLabelForItem(obj) : "";
          if (shortLabel.length) {
            rm.set("shortLabel", shortLabel);
          }
        }

        // Force boolean property values to be true/false
        // That way we can use null to represent indeterminate values.
        properties.forEach(prop => {
          if (prop.format === PropertyFormatIdentifier.boolean) {
            rm.setIn(["propertyValues", prop.id], Boolean(obj.propertyValues.get(prop.id)));
          }
        });

        // Denormalize references
        obj.get("references").forEach(rId => {
          const rel = relationships.get(rId);
          // Calculate object age, if required
          let objectAge = null;
          if (startDate) {
            objectAge = getRelationshipObjectAge(startDate, displayItemsById, calendar, rel, false);
          }

          // TODO: Update this to include the alive status
          let relRecord = createFromJS.InspectorRelationshipRecord(rel).merge({
            objectLabel: getItemlabel(displayItemsById, rel.object),
            objectAge
          });
          if (!rm.getIn(["relationshipsByRefType", rel.reference])) {
            rm.setIn(["relationshipsByRefType", rel.reference], List([relRecord]));
          } else {
            rm.updateIn(["relationshipsByRefType", rel.reference], l => l.push(relRecord));
          }
        });

        // Denormalize inverseReferences
        obj.get("inverseReferences").forEach(rId => {
          const rel = relationships.get(rId);

          const reference = references.get(rel.reference);

          let subjectAge = null;
          // Calculate object age, if required
          if (startDate) {
            subjectAge = getRelationshipObjectAge(startDate, displayItemsById, calendar, rel, true);
          }

          // TODO: Update this to include the alive status
          let relRecord = createFromJS.InspectorRelationshipRecord(rel).merge({
            objectLabel: getItemlabel(displayItemsById, rel.subject),
            objectAge: subjectAge,
            object: reference.bidirectional ? rel.subject : rel.object,
            subject: reference.bidirectional ? rel.object : rel.subject
          });
          if (reference.bidirectional) {
            if (!rm.getIn(["relationshipsByRefType", rel.reference])) {
              rm.setIn(["relationshipsByRefType", rel.reference], List([relRecord]));
            } else {
              rm.updateIn(["relationshipsByRefType", rel.reference], l => l.push(relRecord));
            }
          } else {
            if (!rm.getIn(["inverseRelationshipsByRefType", rel.reference])) {
              rm.setIn(["inverseRelationshipsByRefType", rel.reference], List([relRecord]));
            } else {
              rm.updateIn(["inverseRelationshipsByRefType", rel.reference], l => l.push(relRecord));
            }
          }
        });

        // Denormalise dependencies
        rm.set(
          "blockingDependencies",
          obj.blockingDependencies
            .map(dId => {
              const dep = validatedDependencies.get(dId);
              if (!dep) {
                return null;
              }

              const other = dep.object;
              const otherItem = displayItemsById.get(other);
              const resolutionOptions = getRelativeResolutionOptions(obj, otherItem);

              return createFromJS.InspectorDependencyRecord(dep).merge({
                ids: List([dId]),
                other,
                otherLabel: getItemlabel(displayItemsById, other),
                resolutionOptions
              });
            })
            .filter(dep => dep)
            .toList()
            .sort((a, b) => globalItemIndexes.get(a.other) - globalItemIndexes.get(b.other))
        );

        rm.set(
          "blockedByDependencies",
          obj.blockedByDependencies
            .map(dId => {
              const dep = validatedDependencies.get(dId);
              if (!dep) {
                return null;
              }
              const other = dep.subject;
              const otherItem = displayItemsById.get(other);
              const resolutionOptions = getRelativeResolutionOptions(obj, otherItem);
              return createFromJS.InspectorDependencyRecord(dep).merge({
                ids: List([dId]),
                other,
                otherLabel: getItemlabel(displayItemsById, other),
                resolutionOptions
              });
            })
            .filter(dep => dep)
            .toList()
            .sort((a, b) => globalItemIndexes.get(a.other) - globalItemIndexes.get(b.other))
        );

        // Denormalise constraints
        rm.set(
          "dateConstraints",
          obj.dateConstraints
            .reduce((list, cId) => {
              return validatedConstraints.get(cId)
                ? list.push(
                  getDenormalisedConstraintRecordForItemWithoutState(
                    displayItemsById,
                    calendar,
                    obj,
                    validatedConstraints.get(cId)
                  )
                )
                : list;
            }, List())
            .sort((a, b) => {
              // If both are relative to another item, sort by that item
              if (a.other && b.other) {
                return globalItemIndexes.get(a.other) - globalItemIndexes.get(b.other);
              }
              if (a.other) {
                return -1;
              }
              if (b.other) {
                return 1;
              }
              if (a.date && b.date) {
                return a.date.timestamp - b.date.timestamp;
              }
              if (a.date) {
                return -1;
              }
              if (b.date) {
                return 1;
              }
              return 0;
            })
        );

        // Convert color ref to swatchColor
        const foundColor = colorMap.get(obj.colorId);
        rm.set("swatchColor", foundColor ? foundColor.swatchColor : obj.color);
        rm.set("color", foundColor ? foundColor.id : obj.color);

        // Find narrative position for object
        const narrativePosition = narrativeSelectors.getNarrativePositionForItem(narratives, obj.id);
        rm.set("narrativePosition", narrativePosition);
        rm.set("narrativeLabel", obj.narrativeLabel);
        rm.set("narrativeAutoLabel", obj.narrativeAutoLabel);
        rm.set("narrativeDisplayLabel", obj.narrativeDisplayLabel);
        rm.set("narrativePathLabel", obj.narrativePathLabel);
        rm.set("isInNarrative", Boolean(narrativePosition.size));
        rm.set("image", obj.image);
        rm.set("attachments", obj.attachments);
        rm.set("links", obj.links);
        rm.set("locationUri", obj.locationUri);
      });
      return createFromJS.InspectorItemRecord(recmap);
    });
    return result;
  }
);

const getRelationshipObjectAge = (startDate, displayItemsById, calendar, rel, inverse) => {
  let objectAge = null;
  if (isDateValid(startDate)) {
    const objStartTs = getItemStartDate(displayItemsById, inverse ? rel.subject : rel.object);
    if (objStartTs.timestamp) {
      const itemIsOngoing = getItemIsOngoing(displayItemsById, inverse ? rel.subject : rel.object);
      const objDuration = getItemDuration(displayItemsById, inverse ? rel.subject : rel.object);
      const objEndTs = dateByAddingSpan(calendar, objStartTs, objDuration);
      if (
        !itemIsOngoing &&
        objEndTs.timestamp &&
        objStartTs.timestamp != objEndTs.timestamp &&
        objEndTs.timestamp <= startDate.timestamp
      ) {
        const ageSpan = dateSubtractionFunctions.timespanBetweenDates(
          calendar,
          dateWithTimestamp(calendar, objEndTs.timestamp, objEndTs.precision),
          startDate
        );
        objectAge = stringFromTimespan(ageSpan, 2) + " since end";
      } else if (startDate.timestamp < objStartTs.timestamp) {
        const ageSpan = dateSubtractionFunctions.timespanBetweenDates(
          calendar,
          startDate,
          dateWithTimestamp(calendar, objStartTs.timestamp, objStartTs.precision)
        );
        objectAge = stringFromTimespan(ageSpan, 2) + " before start";
      } else {
        const ageSpan = dateSubtractionFunctions.timespanBetweenDates(
          calendar,
          dateWithTimestamp(calendar, objStartTs.timestamp, objStartTs.precision),
          startDate
        );
        objectAge = stringFromTimespan(ageSpan, 2);
      }
    }
  }
  return objectAge;
};

const getDenormalisedReferences = createSelector(
  // inputs
  state => definitionSelectors.getReferencesById(state),
  state => definitionSelectors.getTypesById(state),
  // calculate based on inputs
  (referencesById, typesById) => {
    return referencesById.map(ref =>
      immutableRecords.InspectorReferenceRecord(ref).set(
        "inverseTypes",
        typesById
          .filter(t => t.references.includes(ref.id))
          .map(t => t.id)
          .toList()
      )
    );
  }
);

const getInverseReferencesForType = (referencesById, typeId) =>
  referencesById.filter(ref => (ref.showInverse || ref.bidirectional) && ref.types.includes(typeId)).toList();

const getDenormalisedType = createSelector(
  // inputs
  getTypeFromDefinitions,
  getDenormalisedReferences,
  referenceSelectors.getReferenceAllIds,
  state => definitionSelectors.getPropertiesById(state),
  propertySelectors.getPropertyAllIds,
  // calculate based on inputs
  (type, referencesById, referenceAllIds, propertiesById, propertyAllIds) => {
    const result = immutableRecords.InspectorTypeRecord(type).withMutations(rec => {
      rec.update("references", refs =>
        referenceAllIds.filter(refId => refs.includes(refId)).map(rId => referencesById.get(rId))
      );
      rec.update("properties", props =>
        propertyAllIds.filter(propId => props.includes(propId)).map(pId => propertiesById.get(pId))
      );
      rec.set("inverseReferences", getInverseReferencesForType(referencesById, type.id));
    });
    return result;
  }
);

const getSelectedDenormalisedType = state => {
  const items = getDenormalisedSelectedItems(state);
  let type = items.size > 0 ? items.first().type : "";
  // No type or having different types, set to empty
  // Otherwise, get the DenormalisedType
  type =
    !type || (items.size > 1 && items.slice(1).findIndex(obj => obj.type !== type) !== -1)
      ? ""
      : getDenormalisedType(state, type);
  return type;
};

const getSelectedDenormalisedInspectorType = state => {
  const type = getSelectedDenormalisedType(state);
  let inspectorType = type ? type : createFromJS.InspectorTypeRecord({});
  return inspectorType;
};

const combineListWithSameValues = (lA, lB) => {
  const longList = lA.size > lB.size ? lA : lB;
  const shortList = longList === lA ? lB : lA;
  return longList.filter(value => shortList.includes(value));
};

const deleteNonMatchingValues = (first, second, primKeys, objKeys) => {
  let result = first;
  primKeys.forEach(k => {
    if (!isNil(first.get(k)) && first.get(k) !== second.get(k)) {
      result = result.delete(k);
    }
  });
  objKeys.forEach(k => {
    if (first.get(k) && !first.get(k).equals(second.get(k))) {
      result = result.delete(k);
    }
  });

  return result;
};

const deleteNonMatchingPropertyValues = (first, second) => {
  return first.update("propertyValues", propVals =>
    propVals.map((val, key) => {
      const secondVal = second.propertyValues.get(key);
      return val === secondVal ? val : null;
    })
  );
};

const deleteNonMatchingRelationships = (first, second) => {
  const result = first.update("relationshipsByRefType", relsByRefType =>
    relsByRefType.map((rels, key) => {
      const secondRels = second.relationshipsByRefType.has(key) ? second.relationshipsByRefType.get(key) : List();
      const innerResult = rels
        .filter(r => secondRels && secondRels.find(s => r.reference === s.reference && r.object === s.object))
        .map(r => r.merge({ objectAge: null, id: uniqueId() }));
      return innerResult;
    })
  );
  return result;
};

const deleteNonMatchingInverseRelationships = (first, second) => {
  return first.update("inverseRelationshipsByRefType", relsByRefType =>
    relsByRefType.map((rels, key) => {
      const secondRels = second.inverseRelationshipsByRefType.get(key);
      return rels
        .filter(r => secondRels && secondRels.find(s => r.reference === s.reference && r.subject === s.subject))
        .map(r => r.set("objectAge", null));
    })
  );
};

const deleteNonMatchingBlockingDependencies = (first, second) => {
  const secondDeps = second.blockingDependencies;
  const result = first.update("blockingDependencies", deps =>
    deps
      .map(d1 => {
        const match = secondDeps.find(d2 => d1.other === d2.other && d1.type === d2.type);
        if (match) {
          return d1.update("ids", ids => ids.push(match.ids.first()));
        }
        return null;
      })
      .filter(d1 => d1 !== null)
  );
  return result;
};

const deleteNonMatchingBlockedByDependencies = (first, second) => {
  const secondDeps = second.blockedByDependencies;
  const result = first.update("blockedByDependencies", deps =>
    deps
      .map(d1 => {
        const match = secondDeps.find(d2 => d1.other === d2.other && d1.type === d2.type);
        if (match) {
          return d1.update("ids", ids => ids.push(match.ids.first()));
        }
        return null;
      })
      .filter(d1 => d1 !== null)
  );
  return result;
};

const deleteNonMatchingDateConstraints = (first, second) => {
  const secondConstraints = second.dateConstraints;
  const result = first.update("dateConstraints", constraints =>
    constraints
      .map(d1 => {
        const match = secondConstraints.find(
          d2 =>
            d1.other === d2.other &&
            d1.constraintType === d2.constraintType &&
            d1.thisTarget === d2.thisTarget &&
            d1.otherTarget === d2.otherTarget &&
            d1.comparison === d2.comparison &&
            d1.date.label === d2.date.label &&
            d1.offset.label === d2.offset.label &&
            d1.partPrecision === d2.partPrecision &&
            d1.partValue === d2.partValue
        );
        if (match) {
          return d1.update("ids", ids => ids.push(match.ids.first()));
        }
        return null;
      })
      .filter(d1 => d1 !== null)
  );
  return result;
};

const countViolations = itemData => {
  return (
    itemData.dateConstraints.filter(c => c.isViolated).size +
    itemData.blockingDependencies.filter(c => c.isViolated).size +
    itemData.blockedByDependencies.filter(c => c.isViolated).size
  );
};

// Merge the selected items into a single object of agreed values
// Also need to retrieve the Definitions list for this object
const getInspectorData = createSelector(
  // fetch parameters for last calculate function
  getSelectedDenormalisedInspectorType,
  getDenormalisedSelectedItems,
  // Calculate result
  (type, items) => {
    const itemIds = items.map(o => o.id);
    const primKeys = [
      "color",
      "label",
      "initials",
      "ongoing",
      "hasOngoingDescendants",
      "type",
      "showAnniversary",
      "anniversaryIcon",
      "isDateLocked",
      "hasChildren",
      "image",
      "locationUri",
      "narrativeLabel",
      "narrativeAutoLabel",
      "narrativeDisplayLabel",
      "narrativePathLabel",
      "isInNarrative"
    ];

    const objKeys = [
      "duration",
      "durationToEarliestEnd",
      "preferredDuration",
      "startDate",
      "preferredStartDate",
      "latestStartDate",
      "endDate",
      "earliestEndDate",
      "preferredEndDate",
      "narrativePosition",
      "attachments",
      "links"
    ];
    let itemData = items.size ? items.first() : createFromJS.InspectorItemRecord({});
    let typeData = type;

    items.forEach((o, index) => {
      // Ignore the first value
      if (index === 0) {
        return;
      }
      itemData = deleteNonMatchingValues(itemData, o, primKeys, objKeys);
      itemData = deleteNonMatchingPropertyValues(itemData, o);
      itemData = deleteNonMatchingRelationships(itemData, o);
      itemData = deleteNonMatchingInverseRelationships(itemData, o);
      itemData = deleteNonMatchingBlockingDependencies(itemData, o);
      itemData = deleteNonMatchingBlockedByDependencies(itemData, o);
      itemData = deleteNonMatchingDateConstraints(itemData, o);
      // combine same tags
      itemData = itemData.set("tags", combineListWithSameValues(itemData.tags, o.tags));
    });
    // Special cases
    if (items.size > 0) {
      if (items.every(i => isDateValid(i.startDate))) {
        itemData = itemData.set("hasDates", true);
      }
      if (items.find(i => i.isDateLocked)) {
        itemData = itemData.set("isDateLocked", true);
      }
    }

    return {
      itemIds,
      itemData,
      items,
      typeData,
      violatedConstraintCount: countViolations(itemData)
    };
  }
);

// Get the list of available parents for the inspector dropdown
const getSuperOptions = createSelector(
  // fetch parameters for last calculate function
  getItemsById,
  (state, itemIds, _typeId) => itemIds,
  (state, itemIds) => superAndChildrenSelectors.getDescendantSetForIds(state, itemIds),
  (state, itemIds, typeId) => (typeId ? definitionSelectors.getTypesById(state).get(typeId).superTypes : List()),
  // Calculate result
  (items, itemIds, descendantsOfItemsSet, superTypes) => {
    return items
      .filter(o => superTypes.contains(o.type) && !itemIds.contains(o.id) && !descendantsOfItemsSet.has(o.id))
      .map((obj, _id) =>
        createFromJS.DropdownOptionRecord({
          label: getLabelWithUntitled(obj.label),
          value: obj.id,
          color: obj.swatchColor,
          displayId: obj.displayId
        })
      )
      .toList()
      .sort(sortFunctions.byLabel)
      .unshift(
        createFromJS.DropdownOptionRecord({
          label: "None",
          value: null
        })
      );
  }
);

// Get the list of available dependencies for the inspector dropdown
const getDependencyOptions = createSelector(
  // fetch parameters for last calculate function
  getItemsById,
  (state, itemIds) => itemIds,
  (state, itemIds) => superAndChildrenSelectors.getDescendantSetForIds(state, itemIds),
  typeSelectors.getTimelineTypeIds,
  // Calculate result
  (items, itemIds, descendantsOfItemsSet, allowedTypeIds) => {
    // TODO: Need to block anything that would create a circular dependency
    // So children of a selected item, parents of a selected item, or things that
    // are already dependent on the selected item.
    return items
      .filter(o => !itemIds.contains(o.id) && allowedTypeIds.contains(o.type) && !descendantsOfItemsSet.has(o.id))
      .map((obj, _id) =>
        createFromJS.DropdownOptionRecord({
          label: getLabelWithUntitled(obj.label),
          value: obj.id,
          color: obj.swatchColor,
          displayId: obj.displayId
        })
      )
      .sort(sortFunctions.byLabel)
      .toList();
  }
);

// Get the list of available dependencies for the inspector constraint dropdown.
// This is similar to the getDependencyOptions function above.
// Difference is constraint allows added to descendants items.
const getConstraintDependencyOptions = createSelector(
  getItemsById,
  (state, itemIds) => itemIds,
  typeSelectors.getCanHaveDateTypeIds,
  // Calculate result
  (items, itemIds, allowedTypeIds) => {
    return items
      .filter(o => !itemIds.contains(o.id) && allowedTypeIds.contains(o.type))
      .map((obj, _id) =>
        createFromJS.DropdownOptionRecord({
          label: getLabelWithUntitled(obj.label),
          value: obj.id,
          color: obj.swatchColor,
          displayId: obj.displayId
        })
      )
      .sort(sortFunctions.byLabel)
      .toList();
  }
);

const getRelationshipOptionsByItemType = createSelector(
  // fetch parameters for last calculate function
  getItemsById,
  // Calculate result
  items => {
    const final = Map()
      .withMutations(result => {
        items.forEach(obj => {
          const typeId = obj.type;
          const option = immutableRecords.DropdownOptionRecord({
            label: obj.label,
            value: obj.id,
            displayId: obj.displayId
          });
          if (result.has(typeId)) {
            result.update(typeId, list => list.push(option));
          } else {
            result.set(typeId, List([option]));
          }
        });
      })
      .map(list => list.sort(sortFunctions.byLabel));
    return final;
  }
);

const getSortedItemTypes = createSelector(
  // fetch parameters for last calculate function
  getRelationshipOptionsByItemType,
  typeSelectors.getTypesById,
  typeSelectors.getAllIds,
  // Calculate result
  (relationshipOptionsByItemType, typesById, typeAllIds) => {
    return typeAllIds
      .map(typeId => {
        const type = typesById.get(typeId);
        const hasRelationshipOptions = Boolean(relationshipOptionsByItemType.get(typeId));
        return createFromJS.InspectorRelationshipItemTypeRecord({
          id: type.id,
          label: type.label,
          references: type.references,
          disabled: !hasRelationshipOptions
        });
      })
      .sortBy(type => type.label);
  }
);

// To use in the react-tags
const getTagObjects = tags =>
  tags
    .map((name, id) => {
      return { id, name };
    })
    .toList()
    .toArray();

// To use in the react-tags
const getTagObjectsForTagIds = (tags, tagIds) => getTagObjects(mapPick(tags, tagIds));

// const getTypeOptions = createSelector(
//   // fetch parameters for last calculate function
//   getTypesById,
//   // Calculate result
//   types =>
//     types
//       .map((t, _id) =>
//         createFromJS.DropdownOptionRecord({
//           label: t.label,
//           value: t.id,
//           color: t.color,
//           icon: t.icon
//         })
//       )
//       .toList()
// );

export default {
  getInspectorData,
  // getColorOptions,
  getSuperOptions,
  getDependencyOptions,
  getConstraintDependencyOptions,
  getRelationshipOptionsByItemType,
  getSortedItemTypes,
  getTagObjects,
  getTagObjectsForTagIds
};
